"use client";

import React from "react";
import { motion } from "framer-motion";

export interface CtlHeroBlockProps {
    clientType?: string;
    expertAreas?: string;
    established?: string;
    website?: string;
    websiteUrl?: string;
    badge?: string;
    title?: string;
    titleHighlight?: string;
    description?: string;
    imageSrc?: string;
}

const LaptopMockup = ({ imageSrc }: { imageSrc: string }) => (
    <motion.div 
        initial={{ opacity: 0, x: 40 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-[700px] mx-auto relative group perspective-[1200px]"
    >
        {/* Screen Top Half */}
        <div className="relative rounded-t-[24px] bg-[#0A0A0C] p-[8px] pb-0 border-t border-x border-[#1C1C1F] shadow-2xl z-10 origin-bottom transition-transform duration-700 ease-out group-hover:rotate-x-[2deg]">
            {/* Screen Inner Bezel */}
            <div className="w-full bg-[#1e1e1e] rounded-t-[16px] overflow-hidden relative aspect-[16/10] border border-[#2d2d30]">
                {/* Camera Hole */}
                <div className="absolute top-2 left-1/2 -translate-x-1/2 w-1.5 h-1.5 bg-black rounded-full z-20" />
                
                {/* Screenshot */}
                <img 
                    src={imageSrc} 
                    alt="Desktop Mockup" 
                    className="w-full h-full object-cover object-top"
                />
                
                {/* Glare */}
                <div className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/5 to-white/0 pointer-events-none" />
            </div>
            {/* Bottom Bezel (Chin) */}
            <div className="h-7 w-full bg-black rounded-b-[8px] relative flex items-center justify-center border-b border-zinc-900">
                <span className="text-[6px] text-zinc-500 font-bold tracking-widest font-sans">KITEBE</span>
            </div>
        </div>

        {/* Base / Keyboard Deck */}
        <div className="relative h-[12px] w-[105%] -ml-[2.5%] bg-gradient-to-b from-[#e5e5ea] to-[#b0b0b5] rounded-b-[16px] shadow-lg z-0 flex justify-center border-t border-white/20">
            {/* Thumb Cutout */}
            <div className="w-24 h-1.5 bg-[#a0a0a5] rounded-b-md" />
        </div>
        
        {/* Shadow */}
        <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 w-[80%] h-12 bg-black/10 blur-2xl z-[-1]" />
    </motion.div>
);

export default function CtlHeroBlock({
    clientType = "CTL Infocom Private Limited",
    expertAreas = "IT Infrastructure, IT Engineering, Cloud Services",
    established = "1994 (ISO 9001-2015 Certified)",
    website = "ctl-website-d3664.web.app",
    websiteUrl = "https://ctl-website-d3664.web.app",
    badge = "Est. 1994 / ISO 9001-2015",
    title = "EFFICIENCY & EXCELLENCE ARE THE SAME.",
    titleHighlight = "THE FUTURE LOOKS HYBRID.",
    description = "We support your pursuit for technology driven growth with powerfully designed IT infrastructure. Since 1994, we have underlined every project with quality and excellence thus enriching our experience and growing our expertise in the area of Information Technology Management, IT Infrastructure Solution and IT Engineering.",
    imageSrc = "/images/ctl_desktop_full.png"
}: CtlHeroBlockProps) {
    return (
        <section className="relative pt-36 pb-20 px-6 md:px-12 lg:px-24 xl:px-32 border-b border-[#EAEAEA] bg-white bg-[radial-gradient(#44aa5205_1px,transparent_1px)] [background-size:32px_32px] text-[#444] font-sans antialiased overflow-x-hidden w-full">
            {/* Background Ambient Glows */}
            <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#44aa52]/3 rounded-full blur-[140px] pointer-events-none" />
            <div className="absolute top-1/2 right-1/4 w-[400px] h-[400px] bg-[#44aa52]/2 rounded-full blur-[120px] pointer-events-none" />

            <div className="container mx-auto z-10 w-full">
                {/* Top Case Study Metadata Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-8 pb-16 border-b border-[#EAEAEA] text-xs font-mono text-zinc-500">
                    <div className="flex flex-col gap-1.5">
                        <span className="text-[#44AA52] uppercase tracking-wider font-semibold">CLIENT / TYPE</span>
                        <span className="text-zinc-800 font-medium">{clientType}</span>
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <span className="text-[#44AA52] uppercase tracking-wider font-semibold">EXPERT AREAS</span>
                        <span className="text-zinc-800 font-medium">{expertAreas}</span>
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <span className="text-[#44AA52] uppercase tracking-wider font-semibold">ESTABLISHED</span>
                        <span className="text-zinc-800 font-medium">{established}</span>
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <span className="text-[#44AA52] uppercase tracking-wider font-semibold">WEBSITE</span>
                        <a 
                            href={websiteUrl} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="text-zinc-800 hover:text-[#44AA52] transition-colors font-medium underline underline-offset-4"
                        >
                            {website}
                        </a>
                    </div>
                </div>

                {/* Main Content & Laptop */}
                <div className="pt-20 pb-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
                    
                    {/* Left: Text */}
                    <div className="lg:col-span-6 space-y-8">
                        {badge && (
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#44aa52]/10 text-[#44aa52] text-xs font-semibold tracking-wider uppercase">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#44aa52] animate-pulse" />
                                {badge}
                            </div>
                        )}
                        <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-5xl xl:text-6xl font-light tracking-tight text-[#1A1A1A] leading-[1.15]">
                            {title} <br />
                            {titleHighlight && (
                                <span className="font-light italic text-[#44AA52]">{titleHighlight}</span>
                            )}
                        </h1>

                        {description && (
                            <p className="text-zinc-650 text-sm md:text-base leading-relaxed max-w-2xl font-light pt-2">
                                {description}
                            </p>
                        )}
                    </div>
                    
                    {/* Right: Laptop Mockup */}
                    {imageSrc && (
                        <div className="lg:col-span-6 relative hidden lg:block">
                            <LaptopMockup imageSrc={imageSrc} />
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}
